/* eslint-disable max-lines-per-function */
import ReplyIcon from '@mui/icons-material/Reply';
import { Alert, AlertTitle, FormControlLabel, Radio, Snackbar, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { deDE } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/de';
import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import type { FormProps } from "react-hook-form";
import { z } from 'zod';
import {
  CheckboxFieldAdapter,
  HookForm as Form,
  MuiDatePickerAdapter,
  MuiLoadingButtonAdapter,
  MuiTextFieldAdapter,
  MuiTimePickerAdapter,
} from '@larvitu/shared/mui/feature-react-hook-form';
import { SubmitButton } from '@larvitu/shared/mui/ui-components';
import { CheckboxField, RadioGroupField } from '@larvitu/shared/mui/ui-form';
import { Link } from '@larvitu/shared/next/mui';

import { postMail } from '../../services/axios/requests';

import type { FC } from 'react';

const SuccessMessage: FC<{formState?: {
    isSubmitSuccessful?: boolean
  }}> = ({
  formState,
}) => {
  const { t } = useTranslation( 'contact' );
  const [isOpen, setIsOpen] = useState( false );

  const handleClose = (): void => setIsOpen( false );

  console.log( formState );
  useEffect( () => {
    setIsOpen( !!formState?.isSubmitSuccessful );
  }, [formState] );

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      open={isOpen}
      autoHideDuration={6000}
      onClose={handleClose}
    >
      <Alert
        role="alert"
        elevation={2}
        severity="success"
        sx={{ width: '100%' }}
        onClose={handleClose}
      >
        <AlertTitle>
          {t( 'success-message.title' )}
        </AlertTitle>
        {t( 'success-message.content' )}
      </Alert>
    </Snackbar>
  );
};

const formSchema = z.object( {
  name: z
    .string( {
      required_error: 'Bitte geben Sie ihren Vornamen an',
    } )
    .min( 1, { message: 'Bitte geben Sie ihren Vornamen an' } )
    .max( 64, { message: 'Ihr Vorname sollte nicht mehr als 64 Zeichen haben' } )
    .trim(),
  birthdate: z
    .string( {
      required_error: 'Bitte geben Sie ihr Gurburtsdatum an',
    } )
    .datetime( { message: 'Bitte geben Sie ein vollständiges Datum an', offset: true } )
    .min( 1, { message: 'Bitte geben Sie ihr Gurburtsdatum an' } ),
  email: z
    .string( {
      required_error: 'Bitte geben Sie ihre Email Adresse an',
    } )
    .email( { message: 'ungültige Email Adresse' } )
    .trim(),
  phoneNumber: z
    .string( {
      required_error: 'Bitte geben Sie ihre Telefonnummer an',
    } )
    .min( 1, { message: 'Bitte geben Sie ihre Telefonnummer an' } )
    .trim(),
  insurance: z.union( [z.literal( 'privat' ), z.literal( 'gesetzlich' )] ),
  newPatient: z.boolean(),
  message: z
    .string( {
      required_error: 'Bitte senden Sie uns eine Nachricht mit',
    } )
    .max( 256, {
      message: 'Ihr Nachricht sollte nicht mehr als 256 Zeichen beinhalten',
    } )
    .trim(),
  agreement: z.boolean().refine( (val) => val, {
    message: 'Bitte stimmen Sie der Datenschutzerklärung zu',
  } ),
} );

const defaultValue: z.infer<typeof formSchema> = {
  name: '',
  birthdate: '',
  email: '',
  phoneNumber: '',
  insurance: 'gesetzlich',
  newPatient: false,
  agreement: false,
  message: '',
};

export const ContactHookForm: FC = () => {
  const { t } = useTranslation( 'common' );

  const handleSubmit /*FormProps<z.infer<typeof formSchema>>['onSubmit']*/ = async (data:z.infer<typeof formSchema> & {reset: ()=> void}) => {
    try {
      await postMail( {
        name: data.name,
        birthdate: data.birthdate,
        email: data.email,
        phoneNumber: data.phoneNumber,
        insurance: data.insurance,
        newPatient: data.newPatient,
        message: data.message,
      } );
      data.reset();
    } catch (error) {
      console.error( error );
    }
  };

  return (
    /* Localization for the DatePicker*/
    <LocalizationProvider
      localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
      dateAdapter={AdapterDayjs}
      adapterLocale="de"
    >
      <Form
        defaultValues={defaultValue}
        schema={formSchema}
        mode="onSubmit"
        onValid={handleSubmit}
      >
        <Form.FieldControl name="name">
          <MuiTextFieldAdapter>
            <TextField
              InputLabelProps={{ required: true }}
              label={`${t( 'labels.first-and-lastname', { ns: 'contact' } )}`}
              fullWidth
            />
          </MuiTextFieldAdapter>
        </Form.FieldControl>

        <Form.FieldControl name="birthdate">
          <MuiDatePickerAdapter>
            <DatePicker
              disableFuture
              sx={{ width: '100%' }}
              label={`${t( 'labels.birthDate', { ns: 'contact' } )} *`}
            />
          </MuiDatePickerAdapter>
        </Form.FieldControl>

        <Form.FieldControl name="email">
          <MuiTextFieldAdapter>
            <TextField
              InputLabelProps={{ required: true }}
              label={`${t( 'labels.mail', { ns: 'contact' } )}`}
              fullWidth
            />
          </MuiTextFieldAdapter>
        </Form.FieldControl>

        <Form.FieldControl name="phoneNumber">
          <MuiTextFieldAdapter>
            <TextField
              type="tel"
              InputLabelProps={{ required: true }}
              label={`${t( 'labels.phone', { ns: 'contact' } )}`}
              fullWidth
            />
          </MuiTextFieldAdapter>
        </Form.FieldControl>

        <Form.FieldControl name="insurance">
          {({ field }) => (
            <RadioGroupField
              required
              component="fieldset"
              id="insureance-group"
              defaultValue="gesetzlich"
              aria-labelledby="insureance-group"
              label="Versicherung"
              {...field}
            >
              <FormControlLabel
                value="gesetzlich"
                control={<Radio />}
                label={t( 'legally' )}
              />
              <FormControlLabel
                value="privat"
                control={<Radio />}
                label={t( 'private' )}
              />
            </RadioGroupField>
          )}
        </Form.FieldControl>

        <br />
        <br />

        <Form.FieldControl name="newPatient">
          <CheckboxFieldAdapter>
            <CheckboxField label="Neupatient" />
          </CheckboxFieldAdapter>
        </Form.FieldControl>

        <Form.FieldControl name="message">
          <MuiTextFieldAdapter>
            <TextField
              label={t( 'labels.message', { ns: 'contact' } )}
              fullWidth
              multiline
              rows={4}
              sx={{
                minHeight: '158px',
              }}
            />
          </MuiTextFieldAdapter>
        </Form.FieldControl>

        <Form.Consumer>
          <SuccessMessage />
        </Form.Consumer>

        <Form.FieldControl name="agreement">
          <CheckboxFieldAdapter>
            <CheckboxField
              label={
                <Trans t={t} components={{ Link: <Link href="/datenschutz" /> }}>
                  privacy-policy2
                </Trans>
              }
            />
          </CheckboxFieldAdapter>
        </Form.FieldControl>

        <Form.Submit>
          <MuiLoadingButtonAdapter>
            <SubmitButton
              fullWidth
              loadingPosition="start"
              startIcon={<ReplyIcon />}
            >
              {t( 'send-request' )}
            </SubmitButton>
          </MuiLoadingButtonAdapter>
        </Form.Submit>
      </Form>
    </LocalizationProvider>
  );
};
